import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';

const rest = ['I', 'T', 'H', 'I', 'N'];
const phrases = ['DESIGN.', 'CODE.', 'SHIP.', 'REPEAT.'];

function IntroOverlay() {
  const [show, setShow] = useState(true);
  const [phraseIndex, setPhraseIndex] = useState(0);

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setShow(false);
      return undefined;
    }
    const id = setTimeout(() => setShow(false), 2600);
    return () => clearTimeout(id);
  }, []);

  useEffect(() => {
    if (!show) return undefined;
    const id = setInterval(() => {
      setPhraseIndex((i) => (i + 1) % phrases.length);
    }, 380);
    return () => clearInterval(id);
  }, [show]);

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          key="intro"
          initial={{ y: 0 }}
          exit={{ y: '-100%' }}
          transition={{ duration: 0.95, ease: [0.86, 0, 0.07, 1] }}
          className="intro-overlay"
          aria-hidden="true"
        >
          <div className="grain-noise pointer-events-none absolute inset-0" />

          <div className="pointer-events-none absolute inset-0">
            <motion.div
              initial={{ scale: 0, opacity: 0.6 }}
              animate={{ scale: 8, opacity: 0 }}
              transition={{ duration: 2.4, ease: [0.22, 1, 0.36, 1] }}
              className="absolute left-1/2 top-1/2 h-40 w-40 -translate-x-1/2 -translate-y-1/2 rounded-full border border-white/15"
            />
            <motion.div
              initial={{ scale: 0, opacity: 0.45 }}
              animate={{ scale: 6, opacity: 0 }}
              transition={{ duration: 2.2, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
              className="absolute left-1/2 top-1/2 h-40 w-40 -translate-x-1/2 -translate-y-1/2 rounded-full border border-accent/30"
            />
            <motion.div
              initial={{ scale: 0, opacity: 0.35 }}
              animate={{ scale: 5, opacity: 0 }}
              transition={{ duration: 2.0, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="absolute left-1/2 top-1/2 h-40 w-40 -translate-x-1/2 -translate-y-1/2 rounded-full border border-white/10"
            />

            <motion.div
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: 1.2, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
              className="absolute left-1/2 top-[38%] h-px w-80 origin-center -translate-x-1/2 bg-gradient-to-r from-transparent via-white/30 to-transparent"
            />
            <motion.div
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: 1.4, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="absolute left-1/2 top-[62%] h-px w-72 origin-center -translate-x-1/2 bg-gradient-to-r from-transparent via-accent/50 to-transparent"
            />
          </div>

          <div className="relative flex flex-col items-center gap-5">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.4 }}
              className="flex h-[1em] items-center font-display text-7xl font-bold uppercase leading-none tracking-[-0.04em] md:text-9xl"
            >
              <motion.span
                initial={{ opacity: 0, scale: 0.4, rotate: -8 }}
                animate={{ opacity: 1, scale: 1, rotate: 0 }}
                transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                className="inline-block"
              >
                N
              </motion.span>

              <div className="flex overflow-hidden">
                {rest.map((c, i) => (
                  <motion.span
                    key={`${c}-${i}`}
                    initial={{ y: '110%', opacity: 0 }}
                    animate={{ y: '0%', opacity: 1 }}
                    transition={{
                      duration: 0.55,
                      delay: 0.75 + i * 0.09,
                      ease: [0.22, 1, 0.36, 1]
                    }}
                    className="inline-block"
                  >
                    {c}
                  </motion.span>
                ))}
              </div>

              <motion.span
                initial={{ opacity: 0, scaleY: 0 }}
                animate={{ opacity: [0, 1, 1, 0], scaleY: [0, 1, 1, 0] }}
                transition={{ duration: 1.4, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
                className="ml-2 inline-block h-[0.78em] w-[3px] bg-accent"
                style={{ transformOrigin: 'bottom' }}
              />
            </motion.div>

            <div className="relative mt-1 h-7 w-44 overflow-hidden text-center">
              <AnimatePresence mode="wait">
                <motion.span
                  key={phrases[phraseIndex]}
                  initial={{ y: 22, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  exit={{ y: -22, opacity: 0 }}
                  transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
                  className="absolute inset-0 font-display text-base font-bold uppercase tracking-[0.28em] text-accent"
                >
                  {phrases[phraseIndex]}
                </motion.span>
              </AnimatePresence>
            </div>

            <motion.div
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: 2.4, ease: 'linear' }}
              className="mt-4 h-px w-40 origin-left bg-gradient-to-r from-accent via-white/60 to-transparent"
            />
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default IntroOverlay;
